import { 
    Box, 
    Divider, 
    Grid, 
    Typography, 
    Button,
    Chip,
    Stack,
} from "@mui/material";
import { useState } from "react";
import axiosClient from "../../../lib/axios";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useSnackbar } from 'notistack';
import { IDataConfigDashboard } from "../../../types/api/geral/configuracoes-dashboard";
import LoadingApiQuery from "../../../components/LoadingApiQuery";
import { CertificadoModal } from "./components/CertificadoModal";
import {
    Security,
    Key,
    Lock,
    CheckCircle,
    Error,
} from "@mui/icons-material";
import { usePermission } from "../../../components/PermissionGuard";

export default function Dashboard() {
    const { enqueueSnackbar } = useSnackbar();
    const [openCertificadoModal, setOpenCertificadoModal] = useState(false);
    const [certificadoData, setCertificadoData] = useState({
        publicKey: null as File | null,
        privateKey: null as File | null,
        password: ""
    });
    const hasPermission = usePermission("configuracoes.cadastrar-certificado")
    const queryClient = useQueryClient();

    const { data: resultConfigDashboard, isLoading: isLoadingConfigDashboard } = useQuery({
        queryKey: ["configuracoes-dashboard"],
        queryFn: () => axiosClient.get("/configuracoes/dashboard")
    });

    const certificadoMutation = useMutation({
        mutationFn: (formData: FormData) => 
            axiosClient.post("/atualizar-certificado-digital", formData, {
                headers: { "Content-Type": "multipart/form-data" }
            }),
        onSuccess: () => {
            queryClient.invalidateQueries(["configuracoes-dashboard"]);
            setOpenCertificadoModal(false);
            resetForm();
            enqueueSnackbar("Certificado atualizado com sucesso!", { variant: 'success' });
        },
        onError: (error: any) => {
            console.error("Erro ao salvar certificado:", error);
            enqueueSnackbar(
                error.response?.data?.message || "Erro ao atualizar certificado", 
                { variant: 'error' }
            );
        }
    });

    const { dataListaTitulosReceber }: IConfigDashboard = {
        dataListaTitulosReceber: resultConfigDashboard?.data
    };

    const hasCertificado = dataListaTitulosReceber?.certificado;

    const handleFileUpload = (file: File, key: 'publicKey' | 'privateKey') => {
        if (!file.name.toLowerCase().endsWith('.pem')) {
            return;
        }

        setCertificadoData(prev => ({
            ...prev,
            [key]: file
        }));
    };

    const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCertificadoData(prev => ({
            ...prev,
            password: event.target.value
        }));
    };

    const handleSubmitCertificado = () => {
        const formData = new FormData();
        formData.append("publicKey", certificadoData.publicKey!);
        formData.append("privateKey", certificadoData.privateKey!);
        formData.append("password", certificadoData.password);

        certificadoMutation.mutate(formData);
    };

    const resetForm = () => {
        setCertificadoData({
            publicKey: null,
            privateKey: null,
            password: ""
        });
    };

    const handleCloseModal = () => {
        setOpenCertificadoModal(false);
        resetForm();
    };

    const getStatusColor = () => {
        switch (hasCertificado) {
            case true: return 'success';
            case false: return 'warning';
            default: return 'default';
        }
    };

    const getStatusText = () => {
        switch (hasCertificado) {
            case true: return 'Ativo';
            default: return 'Não Configurado';
        }
    };

    const handleRemoveFile = (key: 'publicKey' | 'privateKey') => {
        setCertificadoData(prev => ({
            ...prev,
            [key]: null
        }));
    };

    return (
        <>
            <CertificadoModal
                openCertificadoModal={openCertificadoModal}
                handleCloseModal={handleCloseModal}
                certificadoData={certificadoData}
                handleFileUpload={handleFileUpload}
                handlePasswordChange={handlePasswordChange}
                handleSubmitCertificado={handleSubmitCertificado}
                handleRemoveFile={handleRemoveFile}
            />

            {isLoadingConfigDashboard && <LoadingApiQuery />}

            {(!isLoadingConfigDashboard && dataListaTitulosReceber) && (
                <Grid container spacing={3}>
                    <Grid size={{xs: 4}}>
                        <Typography>Usuários cadastrados</Typography>
                        <Typography variant="h4" fontWeight="bold">
                            {dataListaTitulosReceber.usuarios_cadastrados}
                        </Typography>
                    </Grid>

                    <Grid size={{xs: 4}}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <Typography>Usuários online</Typography>
                            <Box
                                width={10}
                                height={10}
                                bgcolor="#7BFF13"
                                borderRadius="50%"
                            />
                        </Box>
                        <Typography variant="h4" fontWeight="bold">
                            {dataListaTitulosReceber.usuarios_online}
                        </Typography>
                    </Grid>

                </Grid>
            )}
            
            <Divider sx={{ my: 2 }} />
                    <Grid size={{xs: 12}}>
                            
                                    <Stack 
                                        direction={{ xs: 'column', sm: 'row' }} 
                                        alignItems={{ xs: 'start', sm: 'center' }}
                                        justifyContent="space-between"
                                        spacing={2}
                                    >
                                        <Stack direction="row" alignItems="center" spacing={2}>
                                            <Box
                                                sx={{
                                                    p: 1.5,
                                                    backgroundColor: 'rgba(255,255,255,0.2)',
                                                    borderRadius: 2,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                <Security sx={{ fontSize: 32 }} />
                                            </Box>
                                            
                                            <Box>
                                                <Typography variant="h6" fontWeight="bold">
                                                    Certificado Digital
                                                </Typography>
                                                <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                                                    {hasCertificado ? (
                                                        <CheckCircle sx={{ fontSize: 16, color: '#4CAF50' }} />
                                                    ) : (
                                                        <Error sx={{ fontSize: 16, color: '#ff9800' }} />
                                                    )}
                                                    <Chip
                                                        label={getStatusText()}
                                                        size="small"
                                                        color={getStatusColor()}
                                                        variant="outlined"
                                                        sx={{ 
                                                            fontWeight: 'bold',
                                                            fontSize: '0.7rem'
                                                        }}
                                                    />
                                                </Stack>
                                            </Box>
                                        </Stack>

                                        <Stack direction="row" spacing={1}>
                                            {hasPermission && (
                                                <Button
                                                variant="outlined"
                                                startIcon={hasCertificado ? <Key /> : <Lock />}
                                                onClick={() => setOpenCertificadoModal(true)}
                                            >
                                                {hasCertificado ? 'Atualizar' : 'Adicionar'}
                                            </Button>
                                            )}
                                        </Stack>
                                    </Stack>


                                    
                              
                    </Grid>
        </>
    )
}

interface IConfigDashboard {
    dataListaTitulosReceber: IDataConfigDashboard
}